import type { EventCategory, PublicEvent, User } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import { isAfter, isBefore, isNonNil } from '@shady-pines/types';
import { isEmpty, isNil } from 'lodash';
import type { EventFilter } from './types';

export const eventMatchesFilters = ({
  event,
  query,
  filter,
  categoriesForEvent,
}: {
  event: PublicEvent;
  query: string | null;
  filter: EventFilter | null;
  categoriesForEvent?: EventCategory[];
}) => {
  const searchMatches = (() => {
    if (isEmpty(query) || isNil(query)) {
      return true;
    }

    return (
      event.title.toLowerCase().includes(query) || event.description.toLowerCase().includes(query)
    );
  })();

  if (isNil(filter)) {
    return searchMatches;
  }

  const categoryMatches = (() => {
    if (isEmpty(filter.categories)) {
      return true;
    }

    if (isEmpty(categoriesForEvent) || isNil(categoriesForEvent)) {
      return false;
    }

    return filter.categories.some((category) => categoriesForEvent.some((c) => c.id === category));
  })();

  const venueMatches = (() => {
    if (isEmpty(filter.venues)) {
      return true;
    }

    return isNonNil(event.venue_id) && filter.venues.includes(event.venue_id);
  })();

  const dateRangeMatches = (() => {
    if (isNil(filter.start_date) && isNil(filter.end_date)) {
      return true;
    }

    const endIncluded = isNonNil(filter.end_date)
      ? isBefore(event.start_time, filter.end_date)
      : true;

    const startIncluded = isNonNil(filter.start_date)
      ? isAfter(event.start_time, filter.start_date)
      : true;

    return endIncluded && startIncluded;
  })();

  const statusMatches = (() => {
    if (isEmpty(filter.status)) {
      return true;
    }

    return filter.status.includes(event.status);
  })();

  return searchMatches && categoryMatches && venueMatches && dateRangeMatches && statusMatches;
};

export const calculateEventChanges = (
  originalEvent: {
    categories: Uuid<EventCategory>[];
    users: Uuid<User>[];
  },
  editableEvent: {
    categories?: Uuid<EventCategory>[];
    users?: Uuid<User>[];
  } | null,
) => {
  if (isNil(editableEvent)) {
    return {
      categoriesToAdd: [],
      categoriesToRemove: [],
      usersToAdd: [],
      usersToRemove: [],
    };
  }

  const categoriesToAdd = editableEvent.categories?.filter(
    (id) => !originalEvent.categories.some((c) => c === id),
  );
  const categoriesToRemove = originalEvent.categories.filter(
    (c) => !editableEvent.categories?.includes(c),
  );

  const usersToAdd = editableEvent.users?.filter((i) => !originalEvent.users.some((u) => u === i));
  const usersToRemove = originalEvent.users.filter(
    (u) => !editableEvent.users?.some((i) => i === u),
  );

  return {
    categoriesToAdd: categoriesToAdd ?? [],
    categoriesToRemove: categoriesToRemove,
    usersToAdd: usersToAdd ?? [],
    usersToRemove: usersToRemove,
  };
};
